import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Cloud, Check, Trash2, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function PlatformAdmin() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [signups, setSignups] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const [t, s] = await Promise.all([
        api.get("/platform/tenants"),
        api.get("/platform/signup-requests"),
      ]);
      setTenants(t.data?.tenants || []);
      setSignups(s.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id, slug) => {
    const vm_ip = window.prompt(`VM IP for ${slug}.rustwall.io?`);
    if (!vm_ip) return;
    setBusy(`approve-${id}`);
    try {
      const { data } = await api.post(`/platform/signup-requests/${id}/approve`, { vm_ip });
      toast.success(`Provisioned ${data.slug}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Provision failed");
    } finally { setBusy(""); }
  };

  const reject = async (id) => {
    setBusy(`reject-${id}`);
    try {
      await api.delete(`/platform/signup-requests/${id}`);
      await load();
    } finally { setBusy(""); }
  };

  const deprovision = async (slug) => {
    if (!window.confirm(`Tear down tenant ${slug}? This deletes containers, DNS, Pages, and the tunnel.`)) return;
    setBusy(`depro-${slug}`);
    try {
      await api.delete(`/platform/tenants/${slug}`);
      toast.success(`Deprovisioned ${slug}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Deprovision failed");
    } finally { setBusy(""); }
  };

  if (user && user.role !== "platform_admin") {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 grid place-items-center">
        <div className="text-center">
          <div className="text-zinc-400 font-mono text-sm">PLATFORM ADMIN ONLY</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="border-b border-zinc-800 px-6 py-4 flex items-center gap-3">
        <Cloud className="w-5 h-5 text-cyan-400" />
        <div>
          <div className="font-display font-bold text-lg tracking-tight">PLATFORM ADMIN</div>
          <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-zinc-500">
            tenant lifecycle · signed in as {user?.email}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-10">
        {/* Pending signups */}
        <section>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-3">
            // PENDING SIGNUPS ({signups.filter((s) => s.status === "pending").length})
          </div>
          <div className="border border-zinc-800 rounded-sm overflow-hidden">
            <Header cols={["Slug", "Email", "Plan", "Created", "Status", ""]} />
            {signups.length === 0 && (
              <div className="px-4 py-6 text-zinc-500 text-sm text-center font-mono">No signups yet.</div>
            )}
            {signups.map((s) => (
              <div key={s.request_id} className="grid grid-cols-6 gap-2 px-4 py-3 border-t border-zinc-800 text-xs items-center">
                <div className="font-mono text-cyan-400 whitespace-normal break-words">{s.desired_slug}</div>
                <div className="font-mono text-zinc-300 whitespace-normal break-words">{s.email}</div>
                <div className="font-mono text-zinc-400 uppercase">{s.plan}</div>
                <div className="font-mono text-zinc-500">{(s.created_at || "").slice(0, 19).replace("T", " ")}</div>
                <div className="font-mono uppercase text-[10px] tracking-widest">
                  <span className={
                    s.status === "approved" ? "text-emerald-400"
                    : s.status === "rejected" ? "text-zinc-500"
                    : "text-amber-400"
                  }>{s.status}</span>
                </div>
                <div className="flex gap-1 justify-end">
                  {s.status === "pending" && (
                    <>
                      <button onClick={() => approve(s.request_id, s.desired_slug)} disabled={busy === `approve-${s.request_id}`}
                        data-testid={`approve-${s.desired_slug}`}
                        className="px-2 py-1 bg-cyan-500/20 border border-cyan-500/60 rounded-sm text-cyan-300 text-[10px] font-mono uppercase tracking-widest hover:bg-cyan-500/30 disabled:opacity-50 flex items-center gap-1">
                        {busy === `approve-${s.request_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Approve
                      </button>
                      <button onClick={() => reject(s.request_id)} disabled={busy === `reject-${s.request_id}`}
                        className="px-2 py-1 border border-zinc-700 rounded-sm text-zinc-400 text-[10px] font-mono uppercase tracking-widest hover:text-red-400">
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Active tenants */}
        <section>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-3">
            // ACTIVE TENANTS ({tenants.length})
          </div>
          <div className="border border-zinc-800 rounded-sm overflow-hidden">
            <Header cols={["Slug", "Owner", "VM", "Portal", "Provisioned", ""]} />
            {tenants.length === 0 && (
              <div className="px-4 py-6 text-zinc-500 text-sm text-center font-mono">No tenants provisioned.</div>
            )}
            {tenants.map((t) => (
              <div key={t.slug} className="grid grid-cols-6 gap-2 px-4 py-3 border-t border-zinc-800 text-xs items-center">
                <div className="font-mono text-cyan-400 font-bold whitespace-normal break-words">{t.slug}</div>
                <div className="font-mono text-zinc-300 whitespace-normal break-words">{t.owner_email}</div>
                <div className="font-mono text-zinc-400">{t.vm_ip}</div>
                <a className="font-mono text-cyan-400 underline whitespace-normal break-words" href={t.frontend} target="_blank" rel="noreferrer">
                  {t.slug}.rustwall.io
                </a>
                <div className="font-mono text-zinc-500">{(t.provisioned_at || "").slice(0, 19).replace("T", " ")}</div>
                <div className="flex justify-end">
                  <button onClick={() => deprovision(t.slug)} disabled={busy === `depro-${t.slug}`}
                    data-testid={`deprovision-${t.slug}`}
                    className="px-2 py-1 border border-zinc-700 rounded-sm text-zinc-400 text-[10px] font-mono uppercase tracking-widest hover:text-red-400 hover:border-red-500/40 disabled:opacity-50 flex items-center gap-1">
                    {busy === `depro-${t.slug}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    Tear down
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function Header({ cols }) {
  return (
    <div className="grid grid-cols-6 gap-2 px-4 py-2 bg-zinc-900/60 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
      {cols.map((c, i) => <div key={i}>{c}</div>)}
    </div>
  );
}
